
import { useState, useEffect } from "react";

// Standardized breakpoints
export const BREAKPOINTS = {
  xs: 480,  // Extra small devices
  sm: 640,  // Small phones
  md: 768,  // Large phones and small tablets
  lg: 1024, // Tablets and small laptops
  xl: 1280, // Laptops and desktops
  xxl: 1536  // Large desktops
};

export type ScreenSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | undefined;

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState<boolean>(true); // Start with mobile assumption for SSR
  const [isPhone, setIsPhone] = useState<boolean>(true);
  const [isTablet, setIsTablet] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(false);
  const [screenSize, setScreenSize] = useState<ScreenSize>(undefined);
  const [devicePixelRatio, setDevicePixelRatio] = useState<number>(1);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [isSmallMobile, setIsSmallMobile] = useState<boolean>(true);
  const [isTouchDevice, setIsTouchDevice] = useState<boolean>(false);
  
  useEffect(() => {
    // Check if window is defined (to avoid SSR issues)
    if (typeof window === "undefined") return;
    
    // Detect touch device
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Set orientation
      setOrientation(width > height ? 'landscape' : 'portrait');
      
      // Device pixel ratio for high-DPI screens
      if (window.devicePixelRatio) {
        setDevicePixelRatio(window.devicePixelRatio);
      }
      
      // Set the screen size based on breakpoints
      if (width < BREAKPOINTS.xs) {
        setScreenSize('xs');
        setIsSmallMobile(true);
      }
      else if (width < BREAKPOINTS.sm) {
        setScreenSize('sm');
        setIsSmallMobile(true);
      }
      else if (width < BREAKPOINTS.md) {
        setScreenSize('md');
        setIsSmallMobile(false);
      }
      else if (width < BREAKPOINTS.lg) {
        setScreenSize('lg');
        setIsSmallMobile(false);
      }
      else if (width < BREAKPOINTS.xl) {
        setScreenSize('xl');
        setIsSmallMobile(false);
      }
      else {
        setScreenSize('xxl');
        setIsSmallMobile(false);
      }
      
      // Updated: Consider both phones and tablets as "mobile" for navigation purposes
      // This is the key change - now devices under 1024px (including tablets) will get mobile navigation
      setIsMobile(width < BREAKPOINTS.lg);
      
      // Phone detection (under 640px)
      setIsPhone(width < BREAKPOINTS.sm);
      
      // Tablet detection (between 768px and 1024px)
      setIsTablet(width >= BREAKPOINTS.md && width < BREAKPOINTS.lg);
      
      // Desktop detection (1024px and above)
      setIsDesktop(width >= BREAKPOINTS.lg);
    };
    
    // Set initial value immediately
    checkScreenSize();
    
    // Use passive event listener for better performance
    window.addEventListener('resize', checkScreenSize, { passive: true });
    
    // Listen for orientation changes specifically on mobile devices
    window.addEventListener('orientationchange', checkScreenSize, { passive: true });
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkScreenSize);
      window.removeEventListener('orientationchange', checkScreenSize);
    };
  }, []);
  
  // Make sure we return an object with all the properties consumers expect
  return { 
    isMobile,
    isPhone,
    isTablet,
    isDesktop,
    screenSize,
    devicePixelRatio,
    orientation,
    breakpoints: BREAKPOINTS,
    isSmallMobile,
    isTouchDevice
  };
}
